import {put, call} from 'redux-saga/effects';
import Event from "../../entities/event";
import * as Actions from "../actions/eventActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type EventAction = {
    eventId: string;
    event: Event;
    events: Event[];
};

export function* fetchOneEvent(action: EventAction) {
	try {
		const event: Event = yield call(api.findOne, 'event', action.eventId);

		// Now we can update the user interface
		yield put({type: Actions.EVENT_FETCH_ONE_SUCCESS, eventId: action.eventId, event});
	}
	catch(err) {
		yield put({type: Actions.EVENT_FETCH_ONE_FAILURE, eventId: action.eventId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {EventAction} action
 */
export function* saveEvent(action: EventAction) {
    // console.debug('⮂ EVENT_SAVE saga', action.event?.id);
    
	if (action.event === undefined || action.event === null) {
		console.error('saveEvent received a null');
		throw new Error('saveEvent received a null');
	}

	if (!(action.event instanceof Event)) {
		action.event = new Event(action.event);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'event', action.event);
		if (addResult.isErr()) {
			console.error(`⮂ saveEvent: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EVENT_SAVE_FAILURE, event: action.event, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EVENT_SAVE, event: action.event});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'event', action.event.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.EVENT_SAVE_FAILURE, event: action.event, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {EventAction} action
 * @param {Event[]} action.events
 */
export function* saveManyEvents(action: EventAction) {
    // console.debug('⮂ EVENT_SAVE_MANY saga', action.events.map(event => event.id).join(','));
    
    if (!Array.isArray(action.events)) {
        console.error('saveManyEvents expected an array, received', action.events);
        throw new Error('saveManyEvents expected an array');
    }

	try {
		const toSave: Event[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.events.length; i++) {
			const event = action.events[i];
			event.persisted = false;
			toSave.push(event);
		}

		const addResult = yield call(idb.add, 'event', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyEvents: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EVENT_SAVE_MANY_FAILURE, events: action.events, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EVENT_SAVE_MANY, events: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.EVENT_SAVE_MANY_FAILURE, events: action.events, status: 500, err});

		console.error(`Error saving ${action.events.length} events to database`, err);
	}
}

/**
 * @param {EventAction} action
 */
export function* removeEvent(action: EventAction) {
    const userId = getCurrentUserId();
	if (!userId || action.event === undefined || action.event === null || !(action.event instanceof Event)) {
			console.error('removeEvent expected type Event, received', action.event);
			throw new Error('removeEvent expected type Event');
		}

	try {
		// Mark as deleted
		action.event.status = 'D';
		action.event.addedOn = newHRTimestamp();
		action.event.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'event', action.event);
		if (addResult.isErr()) {
			console.error(`⮂ removeEvent: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EVENT_REMOVE_FAILURE, event: action.event, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EVENT_REMOVE, event: action.event});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.EVENT_REMOVE_FAILURE, event: action.event, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Event.
	*
	* @param {EventAction} action
	*/
export function* restoreEvent(action: EventAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreEvent expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.event === undefined || action.event === null) {
        console.error('restoreEvent expected a Event, received null');
        throw new Error('restoreEvent expected a Event, received null');
    }
    else if (!(action.event instanceof Event)) action.event = new Event(action.event); 
    
	try {
		// Mark as restored
		action.event.status = 'U';
		action.event.addedOn = newHRTimestamp();
		action.event.addedBy = userId;
		action.event.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'event', action.event);
		if (addResult.isErr()) {
			console.error(`⮂ restoreEvent: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EVENT_RESTORE_FAILURE, event: action.event, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EVENT_RESTORE, event: action.event});
	}
	catch(err) {
		yield put({type: Actions.EVENT_RESTORE_FAILURE, event: action.event, status: 500, err});

		console.error('Error restoring from database', err);
	}
}